import { useNavigate } from 'react-router-dom';
import { useAuth } from '@repletoryreach/sdk-auth';
import { Button, Group, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { useT } from '../../i18n/I18nProvider';

/**
 * W31: visible whenever the session is a platform-admin impersonation token.
 * The session is tab-scoped (sessionStorage), never refreshes, and expires
 * within 15 minutes.
 *
 * Rebuilt per `_UI_improvements/tenant/_app-shell.md` §4 item 13: it used to
 * inline its own orange palette, which duplicated colour tokens and did not
 * track theme changes, and every string was hardcoded English in an app that
 * is otherwise fully catalogued. `variant="filled"` on the semantic `warning`
 * colour gives the full-width strip the design reference shows, from tokens.
 */
export function ImpersonationBanner() {
  const { isImpersonating, impersonatedBy, user, endImpersonation } = useAuth();
  const navigate = useNavigate();
  const t = useT();

  if (!isImpersonating) return null;

  async function exit() {
    await endImpersonation();
    // Script-opened tabs (the admin console used window.open) can self-close;
    // otherwise fall back to the login screen.
    window.close();
    navigate('/auth/login', { replace: true });
  }

  return (
    <Alert
      color="warning"
      variant="filled"
      role="status"
      className="rr-impersonation"
      icon={<UserSwitchIcon />}
    >
      <Group justify="space-between" align="center" wrap="nowrap" gap="md">
        <Text role="bodySmall" fw={600} className="rr-impersonation__text">
          {/* The operator is the one signed in; the organisation is what is being viewed (3 October 2026:
              it used to call the operator the person being impersonated). */}
          {t('shell.impersonation.body', {
            operator: impersonatedBy ?? '',
            org: user?.org.name ?? ''
          })}
        </Text>
        <Button
          size="xs"
          variant="default"
          className="rr-impersonation__exit"
          onClick={() => { void exit(); }}
          aria-label={t('shell.impersonation.exitAria')}
        >
          {t('shell.impersonation.exit')}
        </Button>
      </Group>
    </Alert>
  );
}

function UserSwitchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="6" cy="5" r="2.5" />
      <path d="M2 13a4 4 0 018 0" />
      <path d="M11 6h3M12.5 4.5L14 6l-1.5 1.5" />
    </svg>
  );
}
